import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
    EMPTY_ANSWERS,
    EMPTY_CONTENT,
    LETTER_TEMPLATES,
    LetterData,
} from '../../types/letter';
import LetterPreview from '../letter-builder/LetterPreview';

const SHOWCASE_LETTER_IDS = ['letter-formel', 'letter-classic', 'letter-corporate'] as const;

const Projects: React.FC = () => {
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();

    const goAuthenticated = (path: string) => {
        if (isAuthenticated) navigate(path);
        else navigate('/login', { state: { from: path } });
    };

    const letterPreviewData: LetterData = useMemo(
        () => ({
            template: 'letter-formel',
            title: 'Aperçu',
            answers: {
                ...EMPTY_ANSWERS,
                firstName: 'Jean',
                lastName: 'Kouassi',
                email: 'jean.kouassi@email.ci',
                phone: '+225 07 00 00 00 00',
                address: 'Cocody, Abidjan',
                city: 'Abidjan',
                jobTitle: 'Chargé de clientèle',
                company: 'Entreprise CI',
            },
            content: EMPTY_CONTENT,
            style: { primaryColor: '#0d9488', fontFamily: 'Georgia, serif' },
        }),
        []
    );

    const letterShowcase = LETTER_TEMPLATES.filter((t) =>
        (SHOWCASE_LETTER_IDS as readonly string[]).includes(t.id)
    );

    return (
        <section id="modeles" className="py-20 bg-gray-50">
            <div className="container mx-auto px-4 space-y-20">
                {/* —— CV —— */}
                <div>
                    <div className="flex justify-between items-end mb-12">
                        <div>
                            <h4 className="text-blue-600 font-semibold uppercase tracking-wider mb-2">
                                Modèles Populaires
                            </h4>
                            <h2 className="text-4xl font-bold text-gray-900 leading-tight">
                                Découvrez nos derniers <br /> Modèles de CV
                            </h2>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {[3, 4, 5].map((item, index) => (
                            <motion.div
                                key={`cv-${index}`}
                                className="group relative overflow-hidden rounded-2xl cursor-pointer"
                                whileHover={{ y: -5 }}
                                onClick={() => goAuthenticated('/choix-modele')}
                            >
                                <img
                                    src={`/${item}.jpg`}
                                    alt="Modèle CV"
                                    className="w-full h-80 object-cover transition-transform duration-500 group-hover:scale-110"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/90 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-8">
                                    <h3 className="text-white text-xl font-bold mb-2">
                                        Modèle Professionnel
                                    </h3>
                                    <div className="flex justify-between items-center text-blue-200">
                                        <span>Voir le modèle</span>
                                        <ArrowRight size={20} />
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>

                {/* —— Lettres —— */}
                <div>
                    <div className="flex justify-between items-end mb-12">
                        <div>
                            <h4 className="text-teal-600 font-semibold uppercase tracking-wider mb-2">
                                Lettres de motivation
                            </h4>
                            <h2 className="text-4xl font-bold text-gray-900 leading-tight">
                                Nos modèles de <br /> lettres adaptés au marché
                            </h2>
                        </div>
                        <button
                            type="button"
                            onClick={() => goAuthenticated('/creer-lettre')}
                            className="hidden md:inline-flex items-center gap-2 text-sm font-semibold text-teal-700 hover:text-teal-900"
                        >
                            Créer ma lettre <ArrowRight size={16} />
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {letterShowcase.map((t, index) => (
                            <motion.div
                                key={t.id}
                                className="group relative overflow-hidden rounded-2xl cursor-pointer bg-slate-100 h-80"
                                whileHover={{ y: -5 }}
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.08 }}
                                onClick={() => goAuthenticated('/creer-lettre')}
                            >
                                <div className="absolute inset-0 flex justify-center overflow-hidden pt-6 transition-transform duration-500 group-hover:scale-110">
                                    <div
                                        className="pointer-events-none shrink-0 shadow-md"
                                        style={{
                                            width: 794,
                                            height: 1123,
                                            transform: 'scale(0.28)',
                                            transformOrigin: 'top center',
                                        }}
                                    >
                                        <LetterPreview
                                            data={{
                                                ...letterPreviewData,
                                                template: t.id,
                                                style: {
                                                    ...letterPreviewData.style,
                                                    primaryColor: t.hex,
                                                },
                                            }}
                                            template={t.id}
                                            showPlaceholders
                                        />
                                    </div>
                                </div>
                                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/90 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-8">
                                    <h3 className="text-white text-xl font-bold mb-2">{t.name}</h3>
                                    <div className="flex justify-between items-center text-blue-200">
                                        <span>Voir le modèle</span>
                                        <ArrowRight size={20} />
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="mt-20 py-8 bg-blue-600 overflow-hidden transform -skew-y-2">
                <div className="flex items-center space-x-12 animate-marquee whitespace-nowrap text-white font-bold text-2xl uppercase tracking-widest opacity-80">
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                        <React.Fragment key={i}>
                            <span>* CV Moderne</span>
                            <span>* Lettre de motivation</span>
                            <span>* Design Épuré</span>
                            <span>* Professionnel</span>
                        </React.Fragment>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default Projects;
